import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  DollarSign,
  AlertTriangle,
  TrendingDown,
  Calendar,
  RefreshCw,
} from 'lucide-react';

interface TcoAnalysis {
  id: string;
  vehicle_id: string;
  vehicle_name: string;
  analysis_date: string;
  purchase_price: number;
  current_value: number;
  depreciation_total: number;
  fuel_cost_total: number;
  maintenance_cost_total: number;
  insurance_cost_total: number;
  total_cost_of_ownership: number;
  cost_per_mile: number;
  cost_per_month: number;
  total_miles: number;
  months_owned: number;
  replacement_recommended: boolean;
}

interface TcoFleetSummary {
  total_fleet_tco: number;
  avg_cost_per_mile: number;
  replacement_candidates: number;
  avg_age_months: number;
  vehicle_count: number;
  analyses: TcoAnalysis[];
}

const MOCK_SUMMARY: TcoFleetSummary = {
  total_fleet_tco: 284500,
  avg_cost_per_mile: 0.58,
  replacement_candidates: 3,
  avg_age_months: 42,
  vehicle_count: 12,
  analyses: [
    {
      id: '1',
      vehicle_id: 'v1',
      vehicle_name: 'Ford Transit #1',
      analysis_date: '2026-03-10',
      purchase_price: 42000,
      current_value: 28000,
      depreciation_total: 14000,
      fuel_cost_total: 9800,
      maintenance_cost_total: 4200,
      insurance_cost_total: 2700,
      total_cost_of_ownership: 30700,
      cost_per_mile: 0.44,
      cost_per_month: 614,
      total_miles: 69772,
      months_owned: 50,
      replacement_recommended: false,
    },
    {
      id: '2',
      vehicle_id: 'v2',
      vehicle_name: 'Chevy Express #2',
      analysis_date: '2026-03-10',
      purchase_price: 38000,
      current_value: 12000,
      depreciation_total: 26000,
      fuel_cost_total: 14200,
      maintenance_cost_total: 11400,
      insurance_cost_total: 4500,
      total_cost_of_ownership: 56100,
      cost_per_mile: 0.82,
      cost_per_month: 748,
      total_miles: 68415,
      months_owned: 75,
      replacement_recommended: true,
    },
    {
      id: '3',
      vehicle_id: 'v3',
      vehicle_name: 'Ram ProMaster #3',
      analysis_date: '2026-03-10',
      purchase_price: 45000,
      current_value: 35000,
      depreciation_total: 10000,
      fuel_cost_total: 5200,
      maintenance_cost_total: 1800,
      insurance_cost_total: 1500,
      total_cost_of_ownership: 18500,
      cost_per_mile: 0.34,
      cost_per_month: 616,
      total_miles: 54411,
      months_owned: 30,
      replacement_recommended: false,
    },
  ],
};

function getCostPerMileColor(cpm: number): { bg: string; text: string } {
  if (cpm < 0.40) return { bg: 'rgba(16,185,129,0.15)', text: '#10B981' };
  if (cpm < 0.60) return { bg: 'rgba(234,179,8,0.15)', text: '#EAB308' };
  if (cpm < 0.75) return { bg: 'rgba(249,115,22,0.15)', text: '#F97316' };
  return { bg: 'rgba(239,68,68,0.15)', text: '#EF4444' };
}

export default function TcoPage() {
  const [generating, setGenerating] = useState(false);
  const { data, isLoading, refetch } = useApi<TcoFleetSummary>('/api/tco/fleet-summary');
  const d = data ?? MOCK_SUMMARY;

  async function generateAllTco() {
    setGenerating(true);
    try {
      // Generate TCO for each vehicle that has a known ID
      const vehicleIds = d.analyses.map(a => a.vehicle_id);
      await Promise.all(vehicleIds.map(vehicle_id =>
        fetch('/api/tco/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vehicle_id }),
        }),
      ));
      refetch();
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="TCO Calculator"
        description="Total Cost of Ownership and replacement planning per vehicle"
        actions={
          <div className="flex items-center gap-3">
            {isLoading && <Spinner size={16} />}
            <button
              type="button"
              onClick={generateAllTco}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
              style={{ background: '#3B82F6', color: '#fff' }}
            >
              <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Generating…' : 'Regenerate All'}
            </button>
          </div>
        }
      />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={DollarSign}
          label="Avg Cost / Mile"
          value={`$${d.avg_cost_per_mile.toFixed(3)}`}
          accent={d.avg_cost_per_mile >= 0.75 ? 'destructive' : d.avg_cost_per_mile >= 0.60 ? 'warning' : 'success'}
        />
        <StatCard
          icon={AlertTriangle}
          label="Replacement Candidates"
          value={d.replacement_candidates}
          accent={d.replacement_candidates > 0 ? 'warning' : 'success' as const}
        />
        <StatCard
          icon={DollarSign}
          label="Total Fleet TCO"
          value={`$${d.total_fleet_tco.toLocaleString()}`}
        />
        <StatCard
          icon={Calendar}
          label="Avg Vehicle Age"
          value={`${Math.round(d.avg_age_months / 12 * 10) / 10} yr`}
        />
      </div>

      {/* Cost-per-mile legend */}
      <div className="flex items-center gap-6 flex-wrap">
        {[
          { label: '< $0.40 / mi', bg: 'rgba(16,185,129,0.15)', text: '#10B981' },
          { label: '< $0.60 / mi', bg: 'rgba(234,179,8,0.15)', text: '#EAB308' },
          { label: '< $0.75 / mi', bg: 'rgba(249,115,22,0.15)', text: '#F97316' },
          { label: '>= $0.75 / mi', bg: 'rgba(239,68,68,0.15)', text: '#EF4444' },
        ].map(item => (
          <div key={item.label} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full" style={{ background: item.text }} />
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{item.label}</span>
          </div>
        ))}
      </div>

      {/* Vehicle TCO Table */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
            <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
              Vehicle TCO Analysis
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                  {['Vehicle', 'Cost / Mile', 'Fuel Cost', 'Maint. Cost', 'Depreciation', 'Total TCO', 'Status'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.analyses.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                      No TCO analyses yet — click Regenerate All to compute
                    </td>
                  </tr>
                ) : (
                  d.analyses.map(a => {
                    const cpmColor = getCostPerMileColor(a.cost_per_mile);
                    return (
                      <tr key={a.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                        <td className="px-4 py-3">
                          <div className="font-medium" style={{ color: 'var(--foreground)' }}>{a.vehicle_name}</div>
                          <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                            {a.total_miles.toLocaleString()} mi · {a.months_owned} mo owned
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold font-mono"
                            style={{ background: cpmColor.bg, color: cpmColor.text }}
                          >
                            ${a.cost_per_mile.toFixed(3)}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--foreground)' }}>
                          ${a.fuel_cost_total.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--foreground)' }}>
                          ${a.maintenance_cost_total.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--foreground)' }}>
                          ${a.depreciation_total.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs font-semibold" style={{ color: 'var(--foreground)' }}>
                          ${a.total_cost_of_ownership.toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          {a.replacement_recommended ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}
                            >
                              <TrendingDown className="w-3 h-3" />
                              Replace
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}
                            >
                              Good
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
